"use client";

import { useMemo, useState } from "react";
import { DateTime } from "luxon";

import { useQuery } from "@tanstack/react-query";

import { If } from "~/components/v3/Conditional";
import { Button } from "~/components/v3/themed/Button";

import { cn } from "~/utils/fontsConfig";
import { getFormattedTimeStamp, groupAndSortByDate } from "~/utils/time";
import { Calendar } from "~/components/shadcn/Calendar";
import { EventItem } from "~/app/beta/events/_components/EventItem";
import { useSession } from "next-auth/react";
import Spinner from "~/components/themed/spinner";
import { CreateEventButton } from "~/app/beta/home/_components/CreateEventModal";
import { FaPlus } from "react-icons/fa";
import CalendarIcon0Events from "~/app/beta/[communitySlug]/_components/icons/CalendarIcon0Events";
import { getCommunitiesToHostQuery } from "~/app/_components/BottomNavBar";
import {
    getProfilePageEvents,
    GetProfilePageEvents,
} from "~/app/profile/[profileSlug]/actions";

const bgVariants = {
    black: "from-black to-black/0",
    card: "from-v3-card-gray to-v3-card-gray/0",
};

export const UpcomingEventsList = ({
    user,
    filterBy,
}: {
    user: {
        id: string;
        name: string;
        image: string;
    };
    filterBy?: "all" | "hosting" | "attending" | "draft";
}) => {
    const session = useSession();
    const [skip, setSkipCount] = useState(0);
    const [selectedDay, setSelectedDay] = useState<Date>();
    const [events, setEvents] = useState<
        | GetProfilePageEvents["hostingEvents"]
        | GetProfilePageEvents["attendingEvents"]
    >([]);
    const [hasMore, setHasMore] = useState(true);
    const take = 3;

    const { data: communitiesToHost } = getCommunitiesToHostQuery();

    const { isLoading, isRefetching, isFetching } = useQuery({
        queryKey: ["profile-events-upcoming", user.id, filterBy, skip],
        queryFn: async () => {
            return await getProfilePageEvents({
                userId: user.id,
                isPast: false,
                take,
                skipHosting: skip ?? 0,
                skipAttending: skip ?? 0,
                skipDraft: skip ?? 0,
                fetchHosting: filterBy === "hosting" || filterBy === "all",
                fetchAttending: filterBy === "attending" || filterBy === "all",
                fetchDraft: filterBy === "draft" || filterBy === "all",
            });
        },
        refetchOnWindowFocus: false,
        enabled: hasMore,
        onSuccess: (data) => {
            if (filterBy === "hosting") {
                if (data.hostingEvents.length < take) setHasMore(false);
                setEvents((prev) => [...prev, ...data.hostingEvents]);
            } else if (filterBy === "attending") {
                if (data.attendingEvents.length < take) setHasMore(false);
                setEvents((prev) => [...prev, ...data.attendingEvents]);
            } else if (filterBy === "draft") {
                if (data.draftEvents.length < take) setHasMore(false);
                setEvents((prev) => [...prev, ...data.draftEvents]);
            } else {
                const combinedEvents = [
                    ...new Map(
                        [...data.attendingEvents, ...data.hostingEvents].map(
                            (item) => [item.id, item],
                        ),
                    ).values(),
                ];

                if (combinedEvents.length < take) setHasMore(false);
                setEvents((prev) => [...prev, ...combinedEvents]);
            }
        },
    });

    const handleShowMore = () => {
        setSkipCount((prevOffset) => prevOffset + take);
    };

    const filteredEvents = useMemo(() => {
        if (!selectedDay) return events;

        const selectedDate = DateTime.fromJSDate(selectedDay, { zone: "UTC" });
        const startOfDay = selectedDate.startOf("day");
        const endOfDay = selectedDate.endOf("day");

        return events.filter((event) => {
            const eventTime = DateTime.fromJSDate(event.startTime, {
                zone: "UTC",
            });
            return eventTime >= startOfDay && eventTime <= endOfDay;
        });
    }, [selectedDay, events]);

    return (
        <div className="flex w-full flex-col">
            <If condition={(isLoading || isRefetching) && events.length === 0}>
                <Spinner />
            </If>

            {events && (
                <>
                    {!events.length && !isRefetching && !isFetching && (
                        <div className="flex w-full flex-col items-center justify-center gap-2">
                            <span>No {filterBy} events found.</span>

                            <span>Let's change that!</span>
                            <CalendarIcon0Events />
                            <CreateEventButton
                                variant="badge"
                                user={user}
                                communities={communitiesToHost}
                                size="sm"
                                Icon={<FaPlus />}
                            />
                        </div>
                    )}

                    {events.length > 0 && (
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-[2fr_1fr]">
                            <div>
                                {groupAndSortByDate(filteredEvents, "asc").map(
                                    ([date, dateEvents], index) => (
                                        <div key={date}>
                                            <div
                                                className={cn(
                                                    bgVariants["card"],
                                                    "sticky top-20 z-[110] w-full",
                                                    "bg-gradient-to-b py-2",
                                                    "flex items-center gap-2",
                                                )}
                                            >
                                                <span
                                                    className={cn(
                                                        "absolute left-[10px] w-[1px] bg-v3-N-450",
                                                        index === 0
                                                            ? "top-1/2 h-1/2"
                                                            : "top-0 h-full",
                                                    )}
                                                />
                                                <div
                                                    className={cn(
                                                        "z-[1] ml-[5px] h-[10px] w-[10px] rounded-full border-[1px] border-v3-N-400 bg-white",
                                                        DateTime.fromMillis(
                                                            date,
                                                        ).diffNow("days")
                                                            .days <= 0 &&
                                                            "bg-black",
                                                    )}
                                                />
                                                <div className="w-max rounded-full bg-v3-N-500 px-3 py-2 text-[14px]">
                                                    {getFormattedTimeStamp(
                                                        dateEvents[0]!
                                                            .startTime,
                                                        {
                                                            showYear: false,
                                                        },
                                                    )}
                                                </div>
                                            </div>

                                            {dateEvents.map((event) => (
                                                <EventItem
                                                    key={event.id}
                                                    userId={
                                                        session.data?.user.id
                                                    }
                                                    event={event}
                                                />
                                            ))}
                                        </div>
                                    ),
                                )}

                                {events.length > 0 && hasMore && (
                                    <Button
                                        className="mt-4"
                                        onClick={handleShowMore}
                                        variant="tertiary"
                                    >
                                        Show more
                                    </Button>
                                )}
                            </div>

                            <div className="justify-self-center md:justify-self-end">
                                <div className="sticky top-20 rounded-[10px] bg-v3-N-500">
                                    <Calendar
                                        className="max-w-[300px]"
                                        mode="single"
                                        selected={selectedDay}
                                        timeZone={"UTC"}
                                        onSelect={setSelectedDay}
                                        autoFocus
                                        modifiers={{
                                            has_events: (date) => {
                                                return events.some((event) => {
                                                    const eventDate =
                                                        DateTime.fromJSDate(
                                                            event.startTime,
                                                            { zone: "UTC" },
                                                        );
                                                    const calendarDate =
                                                        DateTime.fromJSDate(
                                                            date,
                                                        );
                                                    return eventDate.hasSame(
                                                        calendarDate,
                                                        "day",
                                                    );
                                                });
                                            },
                                        }}
                                        modifiersClassNames={{
                                            has_events: "before:bg-v3-primary",
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
